import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Shield, FileText, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { addActivityLogEntry } from '@/data/mockActivityLog';
import type { Asset, DestructionMethod } from '@/types/models';

interface DestructionCertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assets: Asset[];
  customerName: string;
  workOrderNumber: string;
  customerEmail?: string;
}

const METHODS: { value: DestructionMethod; label: string }[] = [
  { value: 'software_wipe', label: 'Software Wipe (NIST 800-88)' },
  { value: 'degauss', label: 'Degaussing' },
  { value: 'shred', label: 'Physical Shredding' },
  { value: 'crush', label: 'Crushing' },
  { value: 'incinerate', label: 'Incineration' },
];

export default function DestructionCertDialog({
  open, onOpenChange, assets, customerName, workOrderNumber, customerEmail,
}: DestructionCertDialogProps) {
  const [method, setMethod] = useState<DestructionMethod>('software_wipe');
  const [technician, setTechnician] = useState('');
  const [generated, setGenerated] = useState(false);

  const certDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  function handleGenerate() {
    if (!technician.trim()) return;

    // Log destruction for each asset
    assets.forEach((asset) => {
      addActivityLogEntry({
        entity_type: 'asset',
        entity_id: asset.id,
        user_id: 'current_user',
        user_name: technician,
        action: 'destruction_recorded',
        details: `Data destruction: ${METHODS.find((m) => m.value === method)?.label} by ${technician}`,
      });
    });

    setGenerated(true);
    toast.success('Certificate of Destruction generated');
  }

  function handleDownload() {
    toast.success('Downloading certificate PDF...');
  }

  function handleEmail() {
    toast.success(`Certificate emailed to ${customerEmail}`);
  }

  function handleClose() {
    setGenerated(false);
    setTechnician('');
    setMethod('software_wipe');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Certificate of Data Destruction
          </DialogTitle>
        </DialogHeader>

        {!generated ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-3 bg-muted/30">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Customer:</span>{' '}
                  <span className="font-medium text-foreground">{customerName}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Work Order:</span>{' '}
                  <span className="font-mono font-medium text-foreground">{workOrderNumber}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Devices:</span>{' '}
                  <span className="font-medium text-foreground">{assets.length}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>{' '}
                  <span className="font-medium text-foreground">{certDate}</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Destruction Method *</Label>
              <Select value={method} onValueChange={(v) => setMethod(v as DestructionMethod)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Technician Name *</Label>
              <Input
                value={technician}
                onChange={(e) => setTechnician(e.target.value)}
                placeholder="Name of person performing destruction"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Devices Included ({assets.length})</Label>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-border p-2 space-y-1 mt-1">
                {assets.map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50 text-xs">
                    <span className="font-mono text-foreground">{a.internal_barcode}</span>
                    <span className="text-muted-foreground">
                      {a.serial_number || '—'} · {a.category_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleGenerate} disabled={!technician.trim()} className="w-full gap-2">
              <FileText className="h-4 w-4" /> Generate Certificate
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Certificate Preview */}
            <div className="border-2 border-border rounded-lg p-6 bg-card space-y-4">
              <div className="text-center">
                <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                <h2 className="text-lg font-bold text-foreground">Certificate of Data Destruction</h2>
                <p className="text-xs text-muted-foreground mt-1">Issued {certDate}</p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Customer</span>
                  <p className="font-medium text-foreground">{customerName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Work Order</span>
                  <p className="font-mono font-medium text-foreground">{workOrderNumber}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Method</span>
                  <p className="font-medium text-foreground">{METHODS.find((m) => m.value === method)?.label}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Technician</span>
                  <p className="font-medium text-foreground">{technician}</p>
                </div>
              </div>

              <Separator />

              <div className="text-xs">
                <p className="font-medium text-foreground mb-1">Devices ({assets.length})</p>
                <div className="space-y-0.5">
                  {assets.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex justify-between text-muted-foreground">
                      <span className="font-mono">{a.internal_barcode}</span>
                      <span>{a.serial_number || '—'} · {a.category_name}</span>
                    </div>
                  ))}
                  {assets.length > 5 && (
                    <p className="text-muted-foreground">...and {assets.length - 5} more</p>
                  )}
                </div>
              </div>

              <Separator />

              <p className="text-[10px] text-muted-foreground text-center">
                This certifies that the above-listed devices have undergone data destruction
                in accordance with NIST SP 800-88 guidelines.
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownload} className="flex-1 gap-2">
                <Download className="h-4 w-4" /> Download PDF
              </Button>
              {customerEmail && (
                <Button onClick={handleEmail} className="flex-1 gap-2">
                  <Mail className="h-4 w-4" /> Email to Customer
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {generated ? 'Done' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
